import React from 'react';
import { inject, observer } from 'mobx-react';
import { IInjectedProps } from '../../../types';
import styles from './compareShareBookmark.scss';
import FragmentPlaceholder from '../../fragmentPlaceholder/fragmentPlaceholder';
import CompareBtn from './compareBtn';
import ShareIcon from '../../icons/shareIcon';
import LogoService from '../../icons/logoservice';
import TabNavigationHelper from '../../../helper/tabNavigationHelper';
import classNames from 'classnames';

/* eslint-disable @typescript-eslint/no-non-null-assertion */
function CompareShareBookmarkButton(props: ICompareShareBookmarkButton) {
    const tabNavigation: TabNavigationHelper = TabNavigationHelper.instance;
    const { icon, label, onClick } = props;
    return (
        <button onClick={onClick} title={label} ref={(el) => tabNavigation.setFocusAtt(el)}>
            {icon}
            <span>{label}</span>
        </button>
    );
}

interface ICompareShareBookmarkButton {
    icon: React.ReactElement;
    label: string;
    onClick(): void;
}

export function CompareShareBookmarkButtonContainer(props: {
    children: JSX.Element;['data-testid']?: string,
    articleSoldOut?: boolean, title?: string
}) {
    const [childHasButton, setChildHasButton] = React.useState(false);
    const containerRef = React.useRef<HTMLElement>();

    React.useEffect(() => {
        // search for fragment button and set flag
        if (containerRef.current) {
            let ref = containerRef.current;
            let searchingForButton = true;
            while (searchingForButton) {
                if (ref.firstElementChild) {
                    if (ref.firstElementChild?.tagName === 'BUTTON') {
                        searchingForButton = false;
                        setChildHasButton(true);
                    } else {
                        ref = ref.firstElementChild as HTMLElement;
                    }
                } else {
                    searchingForButton = false;
                    setChildHasButton(false);
                }
            }
        }
    }, []);

    return (
        <div
            title={props.title}
            data-testid={props['data-testid']}
            data-semantic-id={props['data-testid']}
            style={{ display: childHasButton ? 'flex' : 'none' }}
            ref={containerRef as React.LegacyRef<HTMLDivElement>}
            className={classNames(styles.compareShareBookmark_button_container,
                { [styles.article_sold_out]: props.articleSoldOut }
            )}
        >
            {props.children}
        </div>
    );
}

const CompareShareBookmark = inject('store')(
    observer(({ store }: IInjectedProps) => {
        if (!store) {
            return null;
        }

        const { dataStore, orderModuleStore, showKioskLayout, localization, fetchedData } = {...store };

        const seoSlug = dataStore.breadcrumb.category.seoSlug ?? '';
        const savKey = dataStore.orderModule.articleInfo.salesArticleVariantKey ?? '';
        const mpcItemOrigin = orderModuleStore.mpcItemOrigin ?? '';

        const logoCreatorEnabled = dataStore.contextData.featureToggles.enableLogoCreator;
        const isSmakeDesignerAvailable = dataStore.shutterModule.productInfo?.description?.isSmakeDesignerAvailable;
        const logoServiceRedirectUrl = dataStore.bannerModule.logoServiceRedirectUrl;
        const logoServiceInfoUrl = fetchedData.shutterModule.productInfo.description.links.logoServiceInfoUrl;
        const shouldShowLogoCreatorButton =
            logoCreatorEnabled &&
            !showKioskLayout &&
            (isSmakeDesignerAvailable || logoServiceRedirectUrl);

        const generalL10n = localization.generalL10n;
        const logoCreatorL10n = localization.logoCreatorL10n;

        function handleShareClick() {
            const shareUrl = window.location.href.split('?')[0];
            const shareTitle = generalL10n.shareLinkText.replace(
                '{0}', dataStore.orderModule.articleInfo.title ?? ''
            );
            const shareText = generalL10n.shareLinkBody
                .replace('{0}', dataStore.orderModule.articleInfo.title ?? '')
                .replace('{1}', generalL10n.companyNameSimple)
                + ' '
                + shareUrl;
            const shareDate: ShareData = {
                title: shareTitle,
                text: shareText,
                url: shareUrl,
            }

            if (navigator.share) {
                navigator.share(shareDate);
            } else {
                window.location.href =
                    'mailto:?subject=' +
                    encodeURIComponent(shareTitle) +
                    '&body=' +
                    encodeURIComponent(shareText);
            }
        }

        const handleLogoCreatorClick = () => {
            if (isSmakeDesignerAvailable) {
                if (logoServiceRedirectUrl) {
                    store.showPersonalizeOverlay();
                } else {
                    store.onPersonalizeIntent();
                }
                return;
            }

            if (logoServiceInfoUrl) {
                window.open(logoServiceInfoUrl, '_self');
            }
        };

        return (
            <div
                data-testid='compareShareBookmark-desktop-container'
                data-semantic-id='compareShareBookmark-desktop-container'
                className={styles.compareShareBookmark_container}
            >
                {dataStore?.contextData?.portalMandantToken != 'GLB' && (
                    <CompareShareBookmarkButtonContainer
                        data-testid='bookmark-button'
                        data-semantic-id='bookmark-button'
                        articleSoldOut={store?.articleSoldOut}
                    >
                        <FragmentPlaceholder
                            src={`/ESPP1-Bookmark/bookmark-add-button?containerId=${store?.getBookmarkContainerID}`}
                            ignoreErrors={true}
                            timeout={2}
                        />
                    </CompareShareBookmarkButtonContainer>
                )}

                {!showKioskLayout && (
                    <CompareShareBookmarkButtonContainer
                        data-testid='share-button'
                        data-semantic-id='share-button'
                        title={generalL10n.shareButtonLocale}
                        articleSoldOut={store?.articleSoldOut}
                    >
                        <CompareShareBookmarkButton
                            icon={<ShareIcon />}
                            label={generalL10n.shareButtonLocale}
                            onClick={handleShareClick}
                        />
                    </CompareShareBookmarkButtonContainer>
                )}

                {!store?.articleSoldOut && (
                    <CompareShareBookmarkButtonContainer
                        data-testid='compare-button'
                        data-semantic-id='compare-button'
                        articleSoldOut={store?.articleSoldOut}
                    >
                        <CompareBtn
                            savKey={savKey}
                            seoSlug={seoSlug}
                            itemOrigin={mpcItemOrigin}
                            data-testid='mpc-button' data-semantic-id='mpc-button'
                        />
                    </CompareShareBookmarkButtonContainer>
                )}

                {shouldShowLogoCreatorButton && (
                    <CompareShareBookmarkButtonContainer
                        data-testid={
                            isSmakeDesignerAvailable
                                ? 'logo-creator-button'
                                : 'no-logo-creator-button'
                        }
                        data-semantic-id={
                            isSmakeDesignerAvailable
                                ? 'logo-creator-button'
                                : 'no-logo-creator-button'
                        }
                        articleSoldOut={store?.articleSoldOut}
                    >
                        <CompareShareBookmarkButton
                            icon={<LogoService />}
                            label={logoCreatorL10n!.personalise}
                            onClick={handleLogoCreatorClick}
                        />
                    </CompareShareBookmarkButtonContainer>
                )}
            </div>
        );
    })
);

export default CompareShareBookmark;
